/**
 * Screen 14b (list) — every programme, drafts included.
 *
 * The member listing hides drafts; this one must not, because a draft is
 * precisely the thing a volunteer came here to finish. Status is a pill in
 * the same words and colours the member sees, so a volunteer on the phone to
 * someone is looking at what they are looking at.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorNote, Note, Shell } from "../../components/Shell";
import type { Locale } from "../../i18n";
import { api, type AdminProgram } from "../../lib/api";
import { formatDateTime, venueWallClockToISO } from "../../lib/format";

/** The name in any language we have, preferring the volunteer's own. A
 *  programme with no text at all is still listed — it is the one most in
 *  need of attention. */
function anyName(program: AdminProgram, locale: Locale, fallback: string): string {
  return (
    program.texts[locale]?.name ??
    program.texts.en?.name ??
    Object.values(program.texts)[0]?.name ??
    fallback
  );
}

export default function AdminPrograms({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<AdminProgram[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [when, setWhen] = useState("");
  const [name, setName] = useState("");
  const [venue, setVenue] = useState("Sangam Hall");
  const [mode, setMode] = useState<"NUMBERED" | "GA">("NUMBERED");

  const load = () =>
    api.admin
      .programs()
      .then((body) => setPrograms(body.programs))
      .catch(() => setError(t("error.generic")));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.admin.createProgram({
        slug: name || undefined,
        // Read as VENUE wall clock, not the browser's. A volunteer typing
        // 19:00 means seven at the hall wherever they happen to be sitting.
        date_time: venueWallClockToISO(when),
        venue,
        seating_mode: mode,
      });
      setName("");
      setWhen("");
      await load();
      window.location.href = `/admin/programs/${created.id}`;
    } catch {
      setError(t("error.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell area="programs" title={t("admin.programs")}>
      <h3 className="section">{t("admin.new_programme")}</h3>
      <div className="stack">
        <input
          className="field"
          value={name}
          placeholder={t("admin.programme_name")}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="field"
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          aria-label={t("program.date")}
        />
        <input
          className="field"
          value={venue}
          placeholder={t("program.venue")}
          onChange={(e) => setVenue(e.target.value)}
        />
        <select
          className="field"
          value={mode}
          onChange={(e) => setMode(e.target.value as "NUMBERED" | "GA")}
          aria-label={t("admin.seating_mode")}
        >
          <option value="NUMBERED">{t("admin.numbered")}</option>
          <option value="GA">{t("seats.general_admission")}</option>
        </select>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!when || busy}
          onClick={create}
        >
          {busy ? t("common.loading") : t("admin.create")}
        </button>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {programs === null && !error && <Note>{t("common.loading")}</Note>}
      {programs?.length === 0 && <Note>{t("programs.none")}</Note>}

      <h3 className="section">{t("admin.programs")}</h3>
      <ul className="cards">
        {(programs ?? []).map((p) => (
          <li key={p.id} className="card">
            <h3 className="card__title">{anyName(p, locale, t("admin.untitled"))}</h3>
            <p className="card__meta card__when">{formatDateTime(p.date_time, locale)}</p>
            <p className="card__meta">{p.venue}</p>
            <p>
              <span className="state" data-state={p.status === "PUBLISHED" ? "PAID" : "HELD"}>
                {p.status === "PUBLISHED" ? t("admin.published") : t("admin.draft")}
              </span>{" "}
              <span className="fallback-note">
                {p.seating_mode === "NUMBERED"
                  ? `${p.seats.sold}/${p.seats.total} ${t("ticket.seat")}`
                  : t("seats.general_admission")}
              </span>
            </p>
            {p.missing_locales.length > 0 && (
              <p className="fallback-note">
                {t("admin.missing_languages", { list: p.missing_locales.join(", ") })}
              </p>
            )}
            <Link className="btn" to={`/admin/programs/${p.id}`}>
              {t("admin.edit")}
            </Link>
          </li>
        ))}
      </ul>

      <Link className="btn" to="/admin">
        {t("common.back")}
      </Link>
    </Shell>
  );
}
